import { useEffect, useState } from 'react';

const MediaRecorder = global.MediaRecorder;

function getDevicesOfKind(deviceInfos, kind) {
  return deviceInfos.filter(function (deviceInfo) {
    return deviceInfo.kind === kind;
  });
}

function ensureMediaPermissions() {
  return navigator.mediaDevices
    .enumerateDevices()
    .then(function (devices) {
      return devices.every(function (device) {
        return !(device.deviceId && device.label);
      });
    })
    .then(function (shouldAskForMediaPermissions) {
      if (shouldAskForMediaPermissions) {
        return navigator.mediaDevices
          .getUserMedia({ audio: true, video: true })
          .then(function (mediaStream) {
            mediaStream.getTracks().forEach(function (track) {
              track.stop();
              // if (track.kind === 'video') {
              //   if (track.enabled) {
              //     track.enabled = false;
              //   }
              // }
            });
          });
      }
    });
}

function saveDeviceSettings(data) {
  try {
    localStorage.setItem('device_settings', JSON.stringify(data));
  } catch (e) {
    console.log(e);
  }
}

function getDeviceSettings() {
  try {
    const d = localStorage.getItem('device_settings');
    if (d) {
      return JSON.parse(d);
    }
  } catch (e) {
    console.log(e);
  }
}

export function getDeviceSelectionOptions() {
  // before calling enumerateDevices, get media permissions (.getUserMedia)
  // w/o media permissions, browsers do not return device Ids and/or labels.
  return ensureMediaPermissions().then(function () {
    return navigator.mediaDevices
      .enumerateDevices()
      .then(function (deviceInfos) {
        var kinds = ['audioinput', 'videoinput'];
        return kinds.reduce(function (deviceSelectionOptions, kind) {
          deviceSelectionOptions[kind] = getDevicesOfKind(deviceInfos, kind);
          return deviceSelectionOptions;
        }, {});
      });
  });
}

export const useMediaRecorder = ({ isRecording, audioOnly = false }) => {
  const [err, setErr] = useState(null);
  const [data, setData] = useState(null);
  const [recorder, setRecorder] = useState(null);
  const [ref, setRef] = useState(null);
  const [stream, setStream] = useState({});
  const [devices, setDevices] = useState();
  const [isSupported, setIsSupported] = useState(undefined);

  useEffect(() => {
    const saved = getDeviceSettings();
    if (saved) {
      setDevices(saved);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (ref && ref.srcObject) {
        removeTracks(ref.srcObject);
      }
    };
  }, [ref]);

  useEffect(() => {
    if (ref) {
      let constraints = { audio: true };
      if (devices?.audioinput) {
        constraints.audio = {
          deviceId: devices.audioinput,
        };
      }
      if (!audioOnly) {
        constraints.video = {
          facingMode: 'user',
          aspectRatio: { ideal: 1.7777777778 },
        };
        if (devices?.videoinput) {
          constraints.video = {
            deviceId: devices.videoinput,
            aspectRatio: { ideal: 1.7777777778 },
          };
        }
      }
      getUserMedia(constraints, setStream, setErr);
    }
  }, [ref, devices]);

  useEffect(() => {
    if (stream.id && ref) {
      ref.srcObject = stream;
      ref.captureStream = ref.captureStream || ref.mozCaptureStream;
      if (!ref.captureStream) {
        setIsSupported(false);
      } else {
        setIsSupported(true);
      }
    }
  }, [stream.id]);

  useEffect(() => {
    if (isRecording) {
      if (!MediaRecorder) {
        setErr('Unsupported browser');
      } else {
        setData(null);
        let chunks = [];
        const mediaRecorder = new MediaRecorder(ref.captureStream(), {
          mimeType: 'video/webm',
        });
        mediaRecorder.ondataavailable = (e) => chunks.push(e.data);
        mediaRecorder.onerror = (e) => setErr(getRecorderError(e.error));
        mediaRecorder.onstop = () => setData(chunks);
        mediaRecorder.start();

        setRecorder(mediaRecorder);
      }
    } else if (recorder) {
      recorder.stop();
    }
  }, [isRecording]);

  const clearRecorder = () => {
    setData(null);
    setRecorder(null);
    setStream({});
  };

  const onSaveDevices = (s) => {
    setDevices(s);
    saveDeviceSettings(s);
  };

  return {
    setCaptureRef: setRef,
    data,
    stream,
    recorder,
    clearRecorder,
    setDevices: onSaveDevices,
    devices,
    err,
    isSupported,
  };
};

async function getUserMedia(constraints, setStream, setErr) {
  try {
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    setStream(stream);
  } catch (err) {
    setErr(getRecorderError(err));
  }
}

function getRecorderError(error) {
  let errMessage;
  switch (error.name) {
    case 'InvalidStateError':
      errMessage = 'An error occurred while recording. Try again later.';
      break;
    case 'NotAllowedError':
      errMessage = 'Camera access denied. Please allow access to record video';
    case 'SecurityError':
      errMessage = 'Recording is not allowed due to security restrictions.';
      break;
    default:
      errMessage = error.message;
      break;
  }

  return errMessage;
}

function getTracks(stream) {
  return stream.getTracks();
}

function removeTrack(stream, track) {
  track.stop();
  stream.removeTrack(track);
}

function removeTracks(stream) {
  try {
    const tracks = getTracks(stream);
    if (tracks && tracks.length > 0) {
      tracks.forEach((track) => {
        removeTrack(stream, track);
      });
    }
  } catch (err) {
    /* eslint-disable */
    console.error(`Error while removing tracks: ${err.message}`);
    /* eslint-enable */
  }
}

export default useMediaRecorder;
